import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useParams } from 'react-router-dom';
import './App.css';
import axios from 'axios';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Notification Context for toast messages
const NotificationContext = createContext();

const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = (message, type = 'info', duration = 5000) => {
    const id = Date.now();
    const notification = { id, message, type, duration };
    setNotifications(prev => [...prev, notification]);

    if (duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, duration);
    }
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  return (
    <NotificationContext.Provider value={{ addNotification, removeNotification }}>
      {children}
      <NotificationContainer notifications={notifications} onRemove={removeNotification} />
    </NotificationContext.Provider>
  );
};

const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

// Notification Container Component
const NotificationContainer = ({ notifications, onRemove }) => {
  if (notifications.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 left-4 sm:left-auto z-50 space-y-2">
      {notifications.map(notification => (
        <div
          key={notification.id}
          className={`w-full sm:max-w-md bg-white shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden transform transition-all duration-300 ${
            notification.type === 'error' ? 'border-l-4 border-red-500' :
            notification.type === 'success' ? 'border-l-4 border-green-500' :
            notification.type === 'warning' ? 'border-l-4 border-yellow-500' :
            'border-l-4 border-blue-500'
          }`}
        >
          <div className="p-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                {notification.type === 'error' && <span className="text-red-500 text-xl">❌</span>}
                {notification.type === 'success' && <span className="text-green-500 text-xl">✅</span>}
                {notification.type === 'warning' && <span className="text-yellow-500 text-xl">⚠️</span>}
                {notification.type === 'info' && <span className="text-blue-500 text-xl">ℹ️</span>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 leading-relaxed">
                  {notification.message}
                </p>
              </div>
              <div className="flex-shrink-0">
                <button
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded"
                  onClick={() => onRemove(notification.id)}
                >
                  <span className="sr-only">Close</span>
                  <span className="text-lg leading-none">×</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Enhanced Auth Context with notifications
const AuthContext = createContext();

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initial token check and validation
  useEffect(() => {
    console.log('=== AUTH PROVIDER INITIAL LOAD ===');
    console.log('Checking localStorage for token...');
    
    // FIRST: Check for any orphaned pre-payment data that could cause contamination
    const prePaymentToken = localStorage.getItem('pre_payment_token');
    const prePaymentUser = localStorage.getItem('pre_payment_user');
    const currentToken = localStorage.getItem('token');
    
    console.log('Current token:', currentToken ? `${currentToken.substring(0, 10)}...` : 'None');
    console.log('Pre-payment token:', prePaymentToken ? `${prePaymentToken.substring(0, 10)}...` : 'None');
    console.log('Pre-payment user:', prePaymentUser ? JSON.parse(prePaymentUser).email : 'None');
    
    // CRITICAL FIX: If there's pre-payment data but no current token, or they don't match, 
    // this means there's orphaned data from a previous session that never got cleaned up
    if (prePaymentToken && (!currentToken || prePaymentToken !== currentToken)) {
      console.error('🚨 ORPHANED PRE-PAYMENT DATA DETECTED!');
      console.error('This is contamination from a previous session that was never cleaned up');
      console.error('Pre-payment token:', prePaymentToken ? `${prePaymentToken.substring(0, 10)}...` : 'None');
      console.error('Current token:', currentToken ? `${currentToken.substring(0, 10)}...` : 'None');
      console.error('FORCE CLEANING ALL DATA to prevent cross-user contamination');
      
      localStorage.clear();
      sessionStorage.clear();
      setToken(null);
      setUser(null);
      setLoading(false);
      
      console.log('✅ Orphaned session data cleared - starting fresh');
      return;
    }
    
    if (currentToken) {
      console.log('Found stored token:', `${currentToken.substring(0, 10)}...`);
      
      // Critical: Always validate the token when app loads
      fetchUserWithToken(currentToken)
        .then(() => {
          console.log('Token validation successful');
          setToken(currentToken);
        })
        .catch((error) => {
          console.error('Token validation failed:', error);
          console.log('Clearing invalid token');
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
          setLoading(false);
        });
    } else {
      console.log('No token found in localStorage');
      setLoading(false);
    }
  }, []);

  // Add axios interceptor to handle authentication errors
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          console.log('=== AXIOS INTERCEPTOR 401 ERROR ===');
          console.log('Current token before clear:', localStorage.getItem('token') ? `${localStorage.getItem('token').substring(0, 10)}...` : 'None');
          console.log('Request URL:', error.config?.url);
          console.log('Error details:', error.response?.data);

          const isAdminRequest = error.config?.url?.includes('/admin/') || error.config?.url?.includes('admin');
          
          if (isAdminRequest) {
            // For admin requests, clear admin token and let AdminDashboard handle redirect
            console.log('Admin authentication error detected, clearing admin session');
            localStorage.removeItem('admin_token');
            // Don't redirect here - let AdminDashboard component handle it
          } else {
            // For regular user requests, clear user token and redirect to home
            console.log('User authentication error detected, clearing user session');
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
            window.location.href = '/';
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);


  const fetchUserWithToken = async (useToken) => {
    try {
      console.log('Fetching user with token:', useToken ? `${useToken.substring(0, 10)}...` : 'None');
      const response = await axios.get(`${API}/user/me`, {
        headers: { Authorization: `Bearer ${useToken}` }
      });
      console.log('User fetched:', response.data.email, 'ID:', response.data.id);
      setUser(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching user with token:', error);
      // If token is invalid, clear everything
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
      setLoading(false);
    }
  };

  const fetchUser = async () => {
    if (token) {
      await fetchUserWithToken(token);
    } else {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      console.log('=== LOGIN ATTEMPT ===');
      console.log('Email:', email);
      console.log('Token before login:', localStorage.getItem('token') ? `${localStorage.getItem('token').substring(0, 10)}...` : 'None');
      
      // CRITICAL: Check for existing user session and warn about overwriting
      const existingToken = localStorage.getItem('token');
      const prePaymentToken = localStorage.getItem('pre_payment_token');
      
      if (existingToken || prePaymentToken) {
        console.log('⚠️ Existing session detected - this will overwrite any previous user session');
        console.log('Existing token:', existingToken ? `${existingToken.substring(0, 10)}...` : 'None');
        console.log('Pre-payment token:', prePaymentToken ? `${prePaymentToken.substring(0, 10)}...` : 'None');
      }
      
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const newToken = response.data.access_token;
      
      console.log('=== LOGIN SUCCESS ===');
      console.log('New token received:', newToken ? `${newToken.substring(0, 10)}...` : 'None');
      
      // AGGRESSIVELY clear ALL old auth and payment data BEFORE setting new data
      console.log('🧹 CLEARING ALL CACHED DATA ON LOGIN (including other users)');
      localStorage.clear(); // Clear everything including old pre-payment data
      sessionStorage.clear();
      
      // Set new authentication data
      localStorage.setItem('token', newToken);
      setToken(newToken);
      
      // Fetch user data with new token
      await fetchUserWithToken(newToken);
      
      console.log('✅ Login complete for user:', response.data.user?.email || email);
      console.log('✅ All old cached data cleared - session is now isolated');
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const register = async (email, password) => {
    try {
      console.log('=== REGISTRATION ATTEMPT ===');
      console.log('Email:', email);
      console.log('Token before register:', localStorage.getItem('token') ? `${localStorage.getItem('token').substring(0, 10)}...` : 'None');
      
      const response = await axios.post(`${API}/auth/register`, { email, password });
      const newToken = response.data.access_token;
      
      console.log('=== REGISTRATION SUCCESS ===');
      console.log('New token received:', newToken ? `${newToken.substring(0, 10)}...` : 'None');
      
      // AGGRESSIVELY clear ALL old auth and payment data
      console.log('🧹 CLEARING ALL CACHED DATA ON REGISTRATION');
      localStorage.clear(); // Clear everything including old pre-payment data
      sessionStorage.clear();
      
      // Set new authentication data
      localStorage.setItem('token', newToken);
      setToken(newToken);
      
      // Fetch user data with new token
      await fetchUserWithToken(newToken);
      
      console.log('✅ Registration complete for user:', response.data.user?.email || email);
      console.log('✅ All old cached data cleared');
      return { success: true };
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  };

  const logout = async () => {
    console.log('=== LOGOUT CALLED ===');
    console.log('Current token before logout:', localStorage.getItem('token') ? `${localStorage.getItem('token').substring(0, 10)}...` : 'None');
    console.log('Current user before logout:', user?.email, user?.id);
    
    // Call backend logout to invalidate token
    try {
      const authToken = localStorage.getItem('token');
      if (authToken) {
        console.log('Calling backend logout...');
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        console.log('✅ Backend logout successful - token invalidated');
      }
    } catch (error) {
      console.log('Backend logout failed (but continuing):', error.message);
    }
    
    // COMPLETE CLEANUP - Clear all storage
    console.log('🧹 COMPLETE AUTH CLEANUP');
    localStorage.clear(); // Clear everything
    sessionStorage.clear(); // Clear everything
    
    // Clear auth context
    setToken(null);
    setUser(null);
    
    console.log('Token after logout:', localStorage.getItem('token'));
    console.log('✅ Complete logout cleanup finished');
    
    // Navigate to home page after a brief delay to ensure state is cleared
    setTimeout(() => {
      window.location.href = '/';
    }, 100);
  };

  const refreshUser = async () => {
    console.log('=== Refreshing User Data ===');
    console.log('Token in context:', token ? `${token.substring(0, 10)}...` : 'None');
    console.log('Token in localStorage:', localStorage.getItem('token') ? `${localStorage.getItem('token').substring(0, 10)}...` : 'None');
    console.log('Current user in context:', user?.email, user?.id);
    
    // CRITICAL SECURITY: Use ONLY the context token for refreshing
    // This prevents token confusion during subscription upgrades
    const activeToken = token;
    
    if (activeToken) {
      console.log('RefreshUser using CONTEXT token only:', `${activeToken.substring(0, 10)}...`);
      
      // Store current user ID for verification
      const currentUserId = user?.id;
      
      try {
        const response = await axios.get(`${API}/user/me`, {
          headers: { Authorization: `Bearer ${activeToken}` }
        });
        
        const fetchedUser = response.data;
        console.log('Fetched user:', fetchedUser.email, 'ID:', fetchedUser.id);
        
        // CRITICAL SECURITY CHECK: Verify user identity hasn't changed
        if (currentUserId && fetchedUser.id !== currentUserId) {
          console.error('🚨 SECURITY ALERT: User ID mismatch during refresh!');
          console.error('Expected user ID:', currentUserId);
          console.error('Fetched user ID:', fetchedUser.id);
          console.error('This indicates a critical security breach - forcing logout');
          
          // Force complete logout and redirect
          localStorage.clear();
          sessionStorage.clear();
          setToken(null);
          setUser(null);
          window.location.href = '/?error=security_breach';
          return;
        }
        
        setUser(fetchedUser);
        setLoading(false);
        console.log('✅ User refresh completed successfully for:', fetchedUser.email);
        
      } catch (error) {
        console.error('Error refreshing user:', error);
        // If token is invalid, clear everything
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        setLoading(false);
      }
    } else {
      console.log('RefreshUser: No context token available - cannot refresh');
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, refreshUser, token, loading, isAuthenticated: !!token }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Components
const LoginForm = ({ onClose }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(email, password);
    if (result.success) {
      onClose();
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Login</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            ✕
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
              required
            />
          </div>

          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50"
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
};

const RegisterForm = ({ onClose }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    const result = await register(email, password);
    if (result.success) {
      onClose();
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Sign Up</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            ✕
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
              required
            />
          </div>

          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2">Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50"
          >
            {loading ? 'Creating Account...' : 'Sign Up & Get 1 Free Credit'}
          </button>
        </form>
      </div>
    </div>
  );
};
const BookCard = ({ book, onPurchase, onGenerate }) => {
  const navigate = useNavigate();

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
      <div className="relative cursor-pointer" onClick={() => navigate(`/book/${book.id}`)}>
        <img
          src={book.cover_image_url?.startsWith('http') ? book.cover_image_url : `${BACKEND_URL}${book.cover_image_url}`}
          alt={book.title}
          className="w-full h-64 object-cover"
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=400&h=600&fit=crop';
          }}
        />
        {book.is_generated && (
          <div className="absolute top-2 right-2 bg-purple-600 text-white px-2 py-1 rounded-full text-xs font-semibold">
            AI Generated
          </div>
        )}
      </div>
      <div className="p-6">
        <h3 
          className="text-xl font-bold text-gray-900 mb-2 cursor-pointer hover:text-purple-600"
          onClick={() => navigate(`/book/${book.id}`)}
        >
          {book.title}
        </h3>
        <p className="text-gray-600 mb-2">by {book.author}</p>
        <p className="text-gray-700 text-sm mb-4 line-clamp-3">{book.synopsis}</p>
        
        <div className="flex justify-between items-center">
          <span className="text-2xl font-bold text-purple-600">${book.price}</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPurchase(book);
            }}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors duration-200"
          >
            Buy Now
          </button>
        </div>
      </div>
    </div>
  );
};

const CategoryFilter = ({ categories, selectedCategory, onSelectCategory }) => (
  <div className="flex flex-wrap gap-2 mb-8">
    {['All', ...categories].map((category) => (
      <button
        key={`store-category-${category}`}
        onClick={() => onSelectCategory(category)}
        className={`px-4 py-2 rounded-full font-medium transition-colors duration-200 ${
          selectedCategory === category
            ? 'bg-purple-600 text-white'
            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
        }`}
      >
        {category}
      </button>
    ))}
  </div>
);

const BookGenerator = ({ categories, onGenerate, isGenerating }) => {
  const [formData, setFormData] = useState({
    category: categories[0] || 'Fiction',
    prompt: ''
  });
  
  const [showcase, setShowcase] = useState(null);
  const [showcaseLoading, setShowcaseLoading] = useState(true);
  
  const { isAuthenticated, user } = useAuth();
  const [showLogin, setShowLogin] = useState(false);

  // Load recent showcase content
  useEffect(() => {
    const loadShowcase = async () => {
      try {
        setShowcaseLoading(true);
        const response = await axios.get(`${API}/recent-showcase`);
        setShowcase(response.data);
      } catch (error) {
        console.error('Error loading showcase:', error);
        // Set fallback showcase content
        setShowcase({
          title: "The Quantum Paradox",
          author: "AI Author",
          category: "Fiction",
          blurb: "Brother Tenzin sat cross-legged on the cold stone floor of the monastery library, an ancient scroll unfurled before him. The parchment was yellowed with age, its edges crumbling despite his careful touch...",
          generator_type: "fiction"
        });
      } finally {
        setShowcaseLoading(false);
      }
    };
    
    loadShowcase();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      setShowLogin(true);
      return;
    }
    
    // Remove frontend credit validation to let backend handle it with fresh data
    // The backend will return proper error if insufficient credits
    
    if (formData.prompt.trim()) {
      onGenerate(formData);
      setFormData({ ...formData, prompt: '' });
    }
  };

  const getTypeColor = (generatorType) => {
    const colors = {
      'fiction': 'bg-blue-500',
      'cookbook': 'bg-orange-500', 
      'textbook': 'bg-green-500',
      'children_book': 'bg-pink-500',
      'nonfiction': 'bg-purple-500'
    };
    return colors[generatorType] || 'bg-gray-500';
  };

  const getTypeEmoji = (generatorType) => {
    const emojis = {
      'fiction': '📖',
      'cookbook': '👨‍🍳', 
      'textbook': '🎓',
      'children_book': '🧸',
      'nonfiction': '📊'
    };
    return emojis[generatorType] || '📚';
  };

  return (
    <>
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl p-8 mb-12 text-white">
        <h2 className="text-3xl font-bold mb-6 text-center">Generate Your AI Book</h2>
        
        {/* Recent Showcase Blurb */}
        {!showcaseLoading && showcase && (
          <div className="bg-white bg-opacity-10 rounded-lg p-6 mb-6 backdrop-blur-sm">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">✨ Recently Generated</h3>
              <span className={`${getTypeColor(showcase.generator_type)} px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1`}>
                {getTypeEmoji(showcase.generator_type)} {showcase.category}
              </span>
            </div>
            <div className="border-l-4 border-white border-opacity-50 pl-4">
              <h4 className="text-xl font-bold mb-2">{showcase.title}</h4>
              <p className="text-sm text-white text-opacity-80 mb-3">by {showcase.author}</p>
              <p className="text-white text-opacity-90 leading-relaxed italic">
                "{showcase.blurb}"
              </p>
              <div className="mt-3 text-center">
                <span className="inline-block bg-white bg-opacity-20 px-4 py-2 rounded-lg text-sm">
                  🎯 <strong>Want something like this?</strong> Try our AI generator below!
                </span>
              </div>
            </div>
          </div>
        )}
        
        {!isAuthenticated && (
          <div className="bg-white bg-opacity-20 rounded-lg p-4 mb-6">
            <p className="text-center text-lg">
              🔒 <strong>Sign up to get 1 free credit</strong> and generate your own custom AI book!
            </p>
          </div>
        )}
        
        {isAuthenticated && (
          <div className="bg-white bg-opacity-20 rounded-lg p-4 mb-6">
            <p className="text-center text-lg">
              ✨ You have <strong>{user?.credits || 0} credits</strong> • Each custom book costs 1 credit
            </p>
            {(user?.plan === 'storage') && (
              <p className="text-center text-sm mt-2 text-white/90">
                🎁 Storage Plan: You get 2 free credits every month!
              </p>
            )}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
          <div className="mb-6">
            <label className="block text-lg font-semibold mb-2">Book Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full p-3 rounded-lg text-gray-900 font-medium"
            >
              {categories.map((category) => (
                <option key={`generator-category-${category}`} value={category}>
                  {category}
                </option>
              ))}
            </select>
            <p className="text-sm text-white/80 mt-2">
              💡 <strong>Smart Categories:</strong> Fiction, Cookbook, and Textbook categories use specialized AI generators for best results!
            </p>
          </div>
          
          <div className="mb-6">
            <label className="block text-lg font-semibold mb-2">Book Concept</label>
            <textarea
              value={formData.prompt}
              onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
              placeholder="Describe your book idea... (e.g., 'A story about a time traveling monk who learns ancient magic')"
              className="w-full p-3 rounded-lg text-gray-900 h-32 resize-none"
              required
            />
            <p className="text-sm text-white/80 mt-2">
              💡 <strong>Tip:</strong> Be specific about characters, plot, recipes, or subject matter for better results!
            </p>
          </div>
          <button
            type="submit"
            disabled={isGenerating}
            className="w-full bg-white text-purple-600 font-bold py-3 px-6 rounded-lg hover:bg-gray-100 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGenerating 
              ? 'Generating Your Book...' 
              : isAuthenticated 
                ? `Generate AI Book (1 Credit)` 
                : 'Sign Up to Generate Books'
            }
          </button>
        </form>
      </div>
      
      {showLogin && <LoginForm onClose={() => setShowLogin(false)} />}
    </>
  );
};

const LoadingSpinner = () => (
  <div className="flex justify-center items-center py-12">
    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600"></div>
  </div>
);

// Book Download Button Component with Separate Format Buttons
const BookDownloadButton = ({ book, user }) => {
  const [downloading, setDownloading] = useState({});
  const { addNotification } = useNotifications();

  const handleDownload = async (format) => {
    if (!user) {
      addNotification('Please log in to download books', 'error');
      return;
    }

    setDownloading(prev => ({ ...prev, [format]: true }));
    
    try {
      const token = localStorage.getItem('token');
      
      console.log(`[DOWNLOAD] Starting ${format} download for book:`, book.id);
      console.log(`[DOWNLOAD] Using token:`, token ? `${token.substring(0, 20)}...` : 'No token');
      
      const response = await fetch(`${API}/books/${book.id}/download/${format}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log(`[DOWNLOAD] Response status:`, response.status);
      console.log(`[DOWNLOAD] Response headers:`, [...response.headers.entries()]);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Download failed' }));
        throw new Error(errorData.detail || `Failed to download ${format.toUpperCase()}`);
      }

      const blob = await response.blob();
      console.log(`[DOWNLOAD] Blob created - size: ${blob.size} bytes, type: ${blob.type}`);
      
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }
      
      // Create download URL
      const url = window.URL.createObjectURL(blob);
      console.log(`[DOWNLOAD] Blob URL created:`, url);
      
      // Create download link
      const a = document.createElement('a');
      a.href = url;
      a.download = `${book.title.replace(/[^a-zA-Z0-9\s]/g, '')}.${format}`;
      a.style.display = 'none';
      
      console.log(`[DOWNLOAD] Download link created with filename:`, a.download);
      
      // Add to DOM and trigger
      document.body.appendChild(a);
      
      // Try multiple trigger methods to ensure download works
      console.log(`[DOWNLOAD] Triggering download...`);
      
      // Method 1: Direct click
      a.click();
      
      // Method 2: Dispatch click event (for some browsers)
      setTimeout(() => {
        const event = new MouseEvent('click', {
          view: window,
          bubbles: true,
          cancelable: true
        });
        a.dispatchEvent(event);
      }, 100);
      
      // Method 3: Focus and simulate enter key (for accessibility)
      setTimeout(() => {
        a.focus();
        const enterEvent = new KeyboardEvent('keydown', {
          key: 'Enter',
          code: 'Enter',
          which: 13
        });
        a.dispatchEvent(enterEvent);
      }, 200);
      
      // Clean up after download should have started
      setTimeout(() => {
        try {
          if (document.body.contains(a)) {
            document.body.removeChild(a);
          }
          window.URL.revokeObjectURL(url);
          console.log(`[DOWNLOAD] Cleanup completed`);
        } catch (cleanupError) {
          console.warn(`[DOWNLOAD] Cleanup error:`, cleanupError);
        }
      }, 2000);
      
      addNotification(`📥 ${format.toUpperCase()} download started! Check your browser's downloads folder.`, 'success');
      console.log(`[DOWNLOAD] Download process completed successfully`);
      
    } catch (error) {
      console.error('[DOWNLOAD] Download failed:', error);
      addNotification(`❌ Download failed: ${error.message}`, 'error');
    } finally {
      setDownloading(prev => ({ ...prev, [format]: false }));
    }
  };

  return (
    <div className="space-y-2">
      <button
        onClick={() => handleDownload('pdf')}
        disabled={downloading.pdf}
        className="w-full bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {downloading.pdf ? (
          <>⏳ Downloading PDF...</>
        ) : (
          <>📕 Download PDF</>
        )}
      </button>
      
      <button
        onClick={() => handleDownload('docx')}
        disabled={downloading.docx}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {downloading.docx ? (
          <>⏳ Downloading DOCX...</>
        ) : (
          <>📄 Download Word</>
        )}
      </button>
      
      <button
        onClick={() => handleDownload('epub')}
        disabled={downloading.epub}
        className="w-full bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {downloading.epub ? (
          <>⏳ Downloading EPUB...</>
        ) : (
          <>📚 Download EPUB</>
        )}
      </button>
    </div>
  );
};

// User Dashboard Component
const UserDashboard = () => {
  const { user, logout, refreshUser, token } = useAuth();
  const navigate = useNavigate();
  const [userBooks, setUserBooks] = useState([]);
  const [generatingBooks, setGeneratingBooks] = useState([]);
  const [filteredBooks, setFilteredBooks] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [userCategories, setUserCategories] = useState([]);
  const [bookStats, setBookStats] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [creditClaimStatus, setCreditClaimStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('books');

  useEffect(() => {
    if (user) {
      loadDashboardData();
      loadGeneratingBooks();
      
      // Set up polling for generating books
      const pollInterval = setInterval(async () => {
        await loadGeneratingBooks();
        
        // Also refresh completed books periodically to catch newly completed books
        if (Math.random() < 0.3) { // Refresh completed books ~30% of the time to avoid too frequent calls
          setTimeout(() => {
            loadDashboardData();
          }, 1000);
        }
      }, 3000); // Poll every 3 seconds for generating books
      
      return () => clearInterval(pollInterval);
    }
  }, [user]); // Remove generatingBooks.length dependency to avoid infinite loops

  // Process categories when books change
  useEffect(() => {
    if (userBooks.length > 0) {
      processUserCategories(userBooks);
    } else {
      setUserCategories([]);
      setFilteredBooks([]);
    }
  }, [userBooks, selectedCategory]);

  const loadGeneratingBooks = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const headers = { Authorization: `Bearer ${token}` };
      
      console.log('🔍 Loading generating books...');
      // Get user's active generation progress
      const response = await axios.get(`${API}/user/generating-books`, { headers });
      const generatingBooksList = response.data.books || [];
      console.log('📚 Found generating books:', generatingBooksList.length);
      setGeneratingBooks(generatingBooksList);
    } catch (error) {
      console.error('Error loading generating books:', error);
      // Don't show error to user for this background operation
      if (error.response?.status !== 404) {
        console.warn('Generating books endpoint may not be working properly');
      }
    }
  };

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('No token found, redirecting to home');
        navigate('/');
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };

      // First verify the token is still valid by getting user data
      try {
        const userResponse = await axios.get(`${API}/user/me`, { headers });
        // Update auth context with latest user data
        if (userResponse.data) {
          // This ensures the user state is fresh
          console.log('Token verified, user:', userResponse.data.email);
        }
      } catch (error) {
        console.error('Token validation failed:', error);
        logout();
        return;
      }

      // Load user's books, stats, subscription, and credit claim status in parallel
      const [booksResponse, statsResponse, subResponse, creditResponse] = await Promise.all([
        axios.get(`${API}/user/books?limit=20`, { headers }),
        axios.get(`${API}/user/books/stats`, { headers }),
        axios.get(`${API}/payments/subscription`, { headers }),
        axios.get(`${API}/payments/can-claim-credits`, { headers }).catch(() => null) // Don't fail if this errors
      ]);

      setUserBooks(booksResponse.data.books);
      setBookStats(statsResponse.data);
      setSubscription(subResponse.data);
      if (creditResponse) {
        setCreditClaimStatus(creditResponse.data);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      // If any API call fails due to auth, logout
      if (error.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  // Process user's books to extract unique categories
  const processUserCategories = (books) => {
    const categories = new Set();
    
    books.forEach(book => {
      if (book.category) {
        categories.add(book.category);
      }
    });
    
    // Convert to array and sort
    const categoriesArray = Array.from(categories).sort();
    
    // Add special categories
    const specialCategories = [];
    
    // Check if user has any generated books
    const hasGeneratedBooks = books.some(book => !book.is_purchased);
    if (hasGeneratedBooks) {
      specialCategories.push('Self Generated');
    }
    
    // Check if user has any purchased books
    const hasPurchasedBooks = books.some(book => book.is_purchased);
    if (hasPurchasedBooks) {
      specialCategories.push('Purchased');
    }
    
    setUserCategories([...specialCategories, ...categoriesArray]);
    filterBooks(books, selectedCategory);
  };

  // Filter books based on selected category
  const filterBooks = (books, category) => {
    let filtered = books;
    
    if (category !== 'All') {
      if (category === 'Self Generated') {
        filtered = books.filter(book => !book.is_purchased);
      } else if (category === 'Purchased') {
        filtered = books.filter(book => book.is_purchased);
      } else {
        filtered = books.filter(book => book.category === category);
      }
    }
    
    setFilteredBooks(filtered);
  };

  // Handle category selection
  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    filterBooks(userBooks, category);
  };

  const handleUpgradeToStorage = async () => {
    console.log('=== UPGRADE TO STORAGE CLICKED ===');
    console.log('Current user before upgrade:', user?.email, user?.id);
    console.log('Current token from useAuth context:', token ? `${token.substring(0, 10)}...` : 'None');
    
    // CRITICAL: Validate authentication before proceeding
    if (!user || !user.email) {
      console.error('❌ No authenticated user found');
      alert('Please log in first to upgrade your storage plan.');
      return;
    }
    
    if (!token) {
      console.error('❌ No authentication token found');
      alert('Authentication required. Please log in again.');
      return;
    }
    
    // Clear any old pre-payment data BEFORE storing new data
    localStorage.removeItem('pre_payment_user');
    localStorage.removeItem('pre_payment_token');
    console.log('🧹 Cleared old pre-payment data');
    
    // Check ALL possible token sources
    console.log('=== CHECKING ALL TOKEN SOURCES ===');
    console.log('localStorage token:', localStorage.getItem('token') ? `${localStorage.getItem('token').substring(0, 10)}...` : 'None');
    console.log('sessionStorage token:', sessionStorage.getItem('token') ? `${sessionStorage.getItem('token').substring(0, 10)}...` : 'None');
    
    // Use the most reliable token source
    const authToken = token || localStorage.getItem('token');
    if (!authToken) {
      console.error('❌ No valid token found in any source');
      alert('Authentication required. Please log in again.');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/payments/create-storage-subscription`, {}, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      console.log('=== STORAGE SUBSCRIPTION RESPONSE ===');
      console.log('Response data:', response.data);
      console.log('About to redirect to Stripe...');
      
      // Store CURRENT user info (with validation)
      const currentUserData = { 
        email: user.email, 
        id: user.id,
        timestamp: Date.now() // Add timestamp for debugging
      };
      localStorage.setItem('pre_payment_user', JSON.stringify(currentUserData));
      localStorage.setItem('pre_payment_token', authToken);
      console.log('✅ Stored pre-payment user data:', currentUserData);
      console.log('✅ Stored pre-payment token:', `${authToken.substring(0, 10)}...`);
      
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error creating storage subscription:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create subscription';
      if (errorMessage.includes('Authentication required')) {
        alert('Please log in again to upgrade your storage plan.');
      } else {
        alert(errorMessage);
      }
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <button
              onClick={() => navigate('/')}
              className="text-purple-600 hover:text-purple-800 font-medium"
            >
              ← Back to Store
            </button>
            <h1 className="text-2xl font-bold text-gray-900">My Dashboard</h1>
            <button
              onClick={logout}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* User Info Card */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start space-y-4 lg:space-y-0">
            {/* User Info Section */}
            <div className="flex-1">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">Welcome back, {user?.email}!</h2>
              
              {/* Stats Grid - Mobile Friendly */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6">
                <div className="bg-purple-50 rounded-lg p-3">
                  <div className="text-xs text-purple-600 font-medium mb-1">Credits</div>
                  <div className="text-lg font-bold text-purple-700">{user?.credits || 0}</div>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <div className="text-xs text-green-600 font-medium mb-1">Plan</div>
                  <div className="text-lg font-bold text-green-700 capitalize">{user?.plan || 'free'}</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-3">
                  <div className="text-xs text-blue-600 font-medium mb-1">Books</div>
                  <div className="text-lg font-bold text-blue-700">{bookStats?.total_books || 0}</div>
                </div>
              </div>
            </div>
            
            {/* Upgrade Section - Mobile Responsive */}
            {user?.plan === 'free' && !subscription?.has_subscription && (
              <div className="bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg p-4 lg:ml-6 lg:max-w-xs">
                <h3 className="font-semibold text-gray-900 mb-2">📚 Storage Plan</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Save & manage your AI books for just <strong>$15/month</strong><br/>
                  <span className="text-purple-600 font-medium">+ Get 2 free book credits every month!</span>
                </p>
                <button
                  onClick={handleUpgradeToStorage}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  Upgrade to Storage
                </button>
              </div>
            )}
            
            {/* Active Subscription Display - Mobile Responsive */}
            {(subscription?.has_subscription || user?.plan === 'storage') && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 lg:ml-6 lg:max-w-xs">
                <h3 className="font-semibold text-green-800 mb-2">✅ Storage Active</h3>
                <p className="text-sm text-green-700 mb-1">
                  Your books are safely stored • $15/month
                </p>
                <p className="text-xs text-green-600">
                  🎁 You receive 2 free book credits monthly
                </p>
                {subscription?.status && (
                  <p className="text-xs text-green-600 mt-1">
                    Status: {subscription.status} • Next billing: {subscription.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : 'N/A'}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Credit Claim Notification Banner - Only for Storage Users */}
        {(user?.plan === 'storage' || subscription?.has_subscription) && creditClaimStatus?.can_claim && (
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-yellow-400 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="text-yellow-600 mr-3">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-yellow-800">🎉 Your Monthly Credits are Ready!</h3>
                  <p className="text-yellow-700">
                    As a Storage subscriber, you get <strong>2 free credits every month</strong>. Click to claim yours now!
                  </p>
                </div>
              </div>
              <button
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    const response = await axios.post(`${API}/payments/claim-monthly-credits`, {}, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    alert(`🎉 ${response.data.message}\nCredits added: ${response.data.credits_added}\nYour new total: ${response.data.new_total} credits`);
                    // Refresh both dashboard data and auth context
                    loadDashboardData();
                    refreshUser();
                  } catch (error) {
                    const errorMsg = error.response?.data?.detail || error.message;
                    if (errorMsg.includes('Only storage subscribers')) {
                      alert('❌ Only storage subscribers can claim monthly credits. Please upgrade to Storage Plan first!');
                    } else {
                      alert('❌ Error claiming credits: ' + errorMsg);
                    }
                  }
                }}
                className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-3 rounded-lg font-semibold text-sm transition-colors duration-200 flex items-center"
              >
                <span className="mr-2">🎁</span>
                Claim 2 Credits
              </button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            {['books', 'stats', 'subscription'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab === 'books' && `My Books (${selectedCategory === 'All' ? userBooks.length : filteredBooks.length})`}
                {tab === 'stats' && 'Statistics'}
                {tab === 'subscription' && 'Subscription'}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'books' && (
          <div>
            {/* Generating Books Section */}
            {generatingBooks.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-600 border-t-transparent mr-2"></div>
                  Books Being Generated ({generatingBooks.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
                  {generatingBooks.map((book) => (
                    <div key={`generating-book-${book.book_id}`} className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg shadow-sm overflow-hidden border-2 border-purple-200">
                      <div className="relative">
                        <div className="w-full h-48 bg-gradient-to-br from-purple-400 to-blue-500 flex items-center justify-center">
                          <div className="text-white text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent mx-auto mb-2"></div>
                            <div className="text-sm font-medium">Generating...</div>
                          </div>
                        </div>
                        {/* Progress Bar Overlay */}
                        <div className="absolute bottom-0 left-0 right-0 bg-white bg-opacity-90 p-2">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-gradient-to-r from-purple-400 to-blue-400 h-2 rounded-full transition-all duration-1000 ease-out"
                              style={{ width: `${book.progress || 0}%` }}
                            ></div>
                          </div>
                          <div className="text-xs text-gray-600 mt-1 text-center">
                            {book.progress || 0}% Complete
                          </div>
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold text-gray-900 mb-1">
                          {book.book?.title || (
                            <span className="text-gray-500 italic">Generating title...</span>
                          )}
                        </h3>
                        <p className="text-sm text-gray-600 mb-2">
                          by {book.book?.author || 'AI Author'}
                        </p>
                        <p className="text-xs text-purple-600 font-medium mb-2">
                          {book.current_step || 'Initializing...'}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <p className="text-xs text-gray-400">
                            Started: {new Date(book.created_at).toLocaleDateString()}
                          </p>
                          <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-800 flex items-center">
                            <div className="animate-spin rounded-full h-3 w-3 border border-purple-600 border-t-transparent mr-1"></div>
                            Generating
                          </span>
                        </div>
                        <div className="mt-3 space-y-2">
                          <button
                            onClick={() => navigate(`/generating/${book.book_id}`)}
                            className="w-full bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded text-sm font-medium"
                          >
                            Watch Progress
                          </button>
                          {/* Disabled download options */}
                          <div className="w-full bg-gray-100 text-gray-400 px-3 py-2 rounded text-sm font-medium text-center cursor-not-allowed">
                            Downloads Available When Complete
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {userBooks.length === 0 && generatingBooks.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📚</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No books generated yet</h3>
                <p className="text-gray-600 mb-4">Start creating your AI book collection!</p>
                <div className="bg-blue-50 rounded-lg p-4 mb-4 max-w-md mx-auto">
                  <p className="text-sm text-blue-800">
                    <strong>📖 What you get:</strong><br/>
                    • Beautiful AI-generated covers<br/>
                    • Compelling book synopses<br/>
                    • Downloadable JSON files<br/>
                    {user?.plan === 'storage' && '• Permanent storage & management'}
                  </p>
                </div>
                <button
                  onClick={() => navigate('/')}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium"
                >
                  Generate Your First Book
                </button>
              </div>
            ) : (
              <div>
                {/* Completed Books Section */}
                {userBooks.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <span className="mr-2">📚</span>
                      Your Library ({userBooks.length})
                    </h3>
                  </div>
                )}
                
                {/* Category Filter */}
                {userCategories.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Filter by Category</h3>
                    <div className="flex flex-wrap gap-2">
                      {['All', ...userCategories].map((category) => (
                        <button
                          key={`user-category-${category}`}
                          onClick={() => handleCategorySelect(category)}
                          className={`px-4 py-2 rounded-full font-medium transition-colors duration-200 ${
                            selectedCategory === category
                              ? 'bg-purple-600 text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          {category}
                          <span className="ml-2 text-xs opacity-75">
                            ({category === 'All' 
                              ? userBooks.length 
                              : category === 'Self Generated' 
                                ? userBooks.filter(b => !b.is_purchased).length
                              : category === 'Purchased'
                                ? userBooks.filter(b => b.is_purchased).length
                                : userBooks.filter(b => b.category === category).length
                            })
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Books Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {(filteredBooks.length > 0 ? filteredBooks : userBooks).map((book) => (
                  <div key={`user-book-${book.id}`} className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                    <img
                      src={book.cover_image_url?.startsWith('http') ? book.cover_image_url : `${BACKEND_URL}${book.cover_image_url}`}
                      alt={book.title}
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=400&h=600&fit=crop';
                      }}
                    />
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-1">{book.title}</h3>
                      <p className="text-sm text-gray-600 mb-2">by {book.author}</p>
                      <p className="text-xs text-gray-500">{book.category}</p>
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-xs text-gray-400">
                          Created: {new Date(book.created_at).toLocaleDateString()}
                        </p>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          book.is_purchased 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {book.is_purchased ? '💰 Purchased' : '🤖 Generated'}
                        </span>
                      </div>
                      <div className="mt-3 space-y-2">
                        <button
                          onClick={() => navigate(`/book/${book.id}`)}
                          className="w-full bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded text-sm font-medium"
                        >
                          View Details
                        </button>
                        {/* Enhanced Download Options */}
                        <BookDownloadButton book={book} user={user} />
                        {user?.plan === 'storage' && (
                          <button
                            onClick={async () => {
                              if (confirm('Are you sure you want to delete this book? This action cannot be undone.')) {
                                try {
                                  const token = localStorage.getItem('token');
                                  await axios.delete(`${API}/user/books/${book.id}`, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  // Refresh books list
                                  loadDashboardData();
                                } catch (error) {
                                  alert('Error deleting book. Please try again.');
                                }
                              }
                            }}
                            className="w-full bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded text-sm font-medium"
                          >
                            🗑️ Delete Book
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'stats' && bookStats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">📊 Overview</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Books:</span>
                  <span className="font-semibold">{bookStats.total_books}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Credits Left:</span>
                  <span className="font-semibold text-purple-600">{bookStats.credits_remaining}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Storage Plan:</span>
                  <span className="font-semibold capitalize">{bookStats.storage_plan}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">📚 By Category</h3>
              <div className="space-y-2">
                {bookStats.categories.map((cat) => (
                  <div key={cat.category} className="flex justify-between">
                    <span className="text-gray-600">{cat.category}:</span>
                    <span className="font-semibold">{cat.count}</span>
                  </div>
                ))}
              </div>
            </div>

            {bookStats.most_recent_book && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">🆕 Latest Book</h3>
                <div className="flex items-center space-x-3">
                  <img
                    src={bookStats.most_recent_book.cover_image_url?.startsWith('http') 
                      ? bookStats.most_recent_book.cover_image_url 
                      : `${BACKEND_URL}${bookStats.most_recent_book.cover_image_url}`}
                    alt={bookStats.most_recent_book.title}
                    className="w-12 h-16 object-cover rounded"
                  />
                  <div>
                    <p className="font-semibold text-sm">{bookStats.most_recent_book.title}</p>
                    <p className="text-xs text-gray-600">{bookStats.most_recent_book.category}</p>
                    <p className="text-xs text-gray-400">
                      {new Date(bookStats.most_recent_book.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'subscription' && (
          <div className="max-w-2xl">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">💳 Subscription Details</h3>
              
              {(subscription?.has_subscription || user?.plan === 'storage') ? (
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
                    <div>
                      <p className="font-semibold text-green-800">Storage Plan Active</p>
                      <p className="text-sm text-green-600">$15.00/month • 2 book credits included monthly</p>
                    </div>
                    <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                      {subscription?.status || 'active'}
                    </span>
                  </div>
                  
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">📚 Storage Plan Benefits</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>✅ Save unlimited AI-generated books</li>
                      <li>✅ 2 free book credits every month</li>
                      <li>✅ Access your books anytime</li>
                      <li>✅ Download and manage your collection</li>
                      <li>✅ Priority customer support</li>
                    </ul>
                    
                    {creditClaimStatus?.can_claim ? (
                      <button
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            const response = await axios.post(`${API}/payments/claim-monthly-credits`, {}, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            alert(`🎉 ${response.data.message}\nCredits added: ${response.data.credits_added}\nYour new total: ${response.data.new_total} credits`);
                            // Refresh both dashboard data and auth context
                            loadDashboardData();
                            refreshUser();
                          } catch (error) {
                            const errorMsg = error.response?.data?.detail || error.message;
                            if (errorMsg.includes('Only storage subscribers')) {
                              alert('❌ Only storage subscribers can claim monthly credits. Please upgrade to Storage Plan first!');
                            } else {
                              alert('❌ Error claiming credits: ' + errorMsg);
                            }
                          }
                        }}
                        className="mt-3 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium"
                      >
                        🎁 Claim Monthly Credits (2 credits)
                      </button>
                    ) : (
                      <div className="mt-3 p-3 bg-gray-100 rounded-lg">
                        <p className="text-sm text-gray-700">
                          📅 <strong>Next Monthly Credits:</strong><br/>
                          {creditClaimStatus?.next_claim_formatted || 'Available next month'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {creditClaimStatus?.reason || 'Credits are allocated monthly'}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {subscription?.current_period_end && (
                    <div className="text-sm text-gray-600">
                      <p>Next billing: {new Date(subscription.current_period_end).toLocaleDateString()}</p>
                      <p>Credits renewed: {new Date(subscription.current_period_end).toLocaleDateString()}</p>
                    </div>
                  )}
                  
                  <button className="text-red-600 hover:text-red-800 text-sm font-medium">
                    Cancel Subscription
                  </button>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-4xl mb-4">📦</div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">No Active Subscription</h4>
                  <p className="text-gray-600 mb-6">
                    Upgrade to Storage Plan to save and manage your AI-generated books permanently.
                  </p>
                  
                  <div className="bg-purple-50 rounded-lg p-6 mb-6">
                    <h5 className="font-semibold text-purple-900 mb-3">📚 Storage Plan Benefits</h5>
                    <ul className="text-sm text-purple-800 space-y-2">
                      <li>✅ Save unlimited AI-generated books</li>
                      <li>✅ 2 free book credits every month</li>
                      <li>✅ Access your books anytime</li>
                      <li>✅ Download and manage your collection</li>
                      <li>✅ Priority customer support</li>
                    </ul>
                  </div>
                  
                  <button
                    onClick={handleUpgradeToStorage}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium"
                  >
                    Upgrade for $15/month
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
const BookDetailPage = () => {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [isOwned, setIsOwned] = useState(false);
  const [ownershipLoading, setOwnershipLoading] = useState(true);

  useEffect(() => {
    loadBook();
    if (user) {
      checkOwnership();
    }
  }, [bookId, user]);

  const loadBook = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/books/${bookId}`);
      setBook(response.data);
    } catch (error) {
      console.error('Error loading book:', error);
      alert('Book not found');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const checkOwnership = async () => {
    try {
      setOwnershipLoading(true);
      const token = localStorage.getItem('token');
      
      // Check if user owns this book (either generated or purchased)
      const response = await axios.get(`${API}/user/books/check-ownership/${bookId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setIsOwned(response.data.is_owned);
    } catch (error) {
      console.error('Error checking ownership:', error);
      // Don't block the UI if ownership check fails
      setIsOwned(false);
    } finally {
      setOwnershipLoading(false);
    }
  };

  const handlePurchase = async () => {
    setPurchasing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/purchase/create-checkout`, {
        book_id: book.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
      
    } catch (error) {
      console.error('Error creating checkout:', error);
      const errorMsg = error.response?.data?.detail || 'Error processing purchase';
      
      // Use notification instead of alert
      if (errorMsg.includes('already own this book')) {
        addNotification('You already own this book! Check your dashboard to access it.', 'warning');
      } else {
        addNotification(`Purchase failed: ${errorMsg}`, 'error');
      }
      setPurchasing(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!book) return <div>Book not found</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/')}
            className="text-purple-600 hover:text-purple-800 font-medium"
          >
            ← Back to Store
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Book Cover */}
          <div className="relative">
            <img
              src={book.cover_image_url?.startsWith('http') ? book.cover_image_url : `${BACKEND_URL}${book.cover_image_url}`}
              alt={book.title}
              className="w-full max-w-md mx-auto rounded-lg shadow-2xl"
              onError={(e) => {
                e.target.src = 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=600&h=800&fit=crop';
              }}
            />
            {book.is_generated && (
              <div className="absolute top-4 right-4 bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                AI Generated
              </div>
            )}
          </div>

          {/* Book Details */}
          <div className="space-y-6">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">{book.title}</h1>
              <p className="text-xl text-gray-600 mb-4">by {book.author}</p>
              <div className="flex items-center space-x-4">
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
                  {book.category}
                </span>
                <span className="text-3xl font-bold text-purple-600">${book.price}</span>
              </div>
            </div>

            {/* Synopsis */}
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Synopsis</h3>
              <p className="text-gray-700 leading-relaxed">{book.synopsis}</p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-4">
              {!user ? (
                <div className="text-center">
                  <p className="text-gray-600 mb-4">Please log in to purchase this book</p>
                  <button
                    onClick={() => navigate('/')}
                    className="w-full bg-gray-600 hover:bg-gray-700 text-white font-bold py-4 px-6 rounded-lg text-lg transition-colors duration-200"
                  >
                    Go to Login
                  </button>
                </div>
              ) : ownershipLoading ? (
                <button
                  disabled
                  className="w-full bg-gray-400 text-white font-bold py-4 px-6 rounded-lg text-lg cursor-not-allowed"
                >
                  Checking ownership...
                </button>
              ) : isOwned ? (
                <div className="space-y-3">
                  <div className="w-full bg-green-100 border border-green-400 text-green-800 font-bold py-4 px-6 rounded-lg text-lg text-center">
                    ✅ You Own This Book
                  </div>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-6 rounded-lg text-lg transition-colors duration-200"
                  >
                    📚 View in My Library
                  </button>
                </div>
              ) : (
                <button
                  onClick={handlePurchase}
                  disabled={purchasing}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-4 px-6 rounded-lg text-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {purchasing ? 'Processing...' : `Buy Now - $${book.price}`}
                </button>
              )}
            </div>

            {/* Book Info */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="font-semibold text-gray-900 mb-3">Book Information</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Category:</span>
                  <span className="text-gray-900">{book.category}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Author:</span>
                  <span className="text-gray-900">{book.author}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Price:</span>
                  <span className="text-gray-900">${book.price}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Type:</span>
                  <span className="text-gray-900">{book.is_generated ? 'AI Generated' : 'Traditional'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Book Generation Progress Page Component
const BookGenerationPage = () => {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pollInterval, setPollInterval] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [estimatedTime, setEstimatedTime] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Early admin detection to bypass AuthProvider issues
  useEffect(() => {
    const userToken = localStorage.getItem('token');
    const adminToken = localStorage.getItem('admin_token');
    const isAdminUser = !!adminToken && !userToken;
    
    console.log('🔍 EARLY ADMIN DETECTION:', {
      hasUserToken: !!userToken,
      hasAdminToken: !!adminToken,
      isAdminUser: isAdminUser
    });
    
    if (isAdminUser) {
      setIsAdmin(true);
    }
  }, []);

  // Pizza tracker style progress steps
  const getProgressSteps = () => {
    // Get book type from progress data to determine appropriate steps
    const bookType = progress?.book?.generator_type || progress?.book?.category?.toLowerCase() || 'fiction';
    
    // Debug logging to see what book type is detected
    console.log('🔍 Book type detection:', {
      generator_type: progress?.book?.generator_type,
      category: progress?.book?.category,
      detected_type: bookType,
      progress_status: progress?.status
    });
    
    // Textbook-specific steps (more academic/structured)
    const textbookSteps = [
      { key: 'initializing', label: 'Course Setup', description: 'Analyzing educational requirements', minProgress: 0 },
      { key: 'schema_generation', label: 'Curriculum Design', description: 'Structuring learning objectives', minProgress: 5 },
      { key: 'plan_generation', label: 'Content Planning', description: 'Organizing chapters and topics', minProgress: 15 },
      { key: 'outline_generation', label: 'Chapter Outline', description: 'Creating detailed structure', minProgress: 30 },
      { key: 'content_generation', label: 'Writing Content', description: 'Developing educational material', minProgress: 50 },
      { key: 'content_expansion', label: 'Adding Examples', description: 'Including practice problems', minProgress: 75 },
      { key: 'image_generation', label: 'Creating Diagrams', description: 'Adding visual aids', minProgress: 90 },
      { key: 'finalizing', label: 'Final Review', description: 'Formatting and polishing', minProgress: 95 },
      { key: 'completed', label: 'Ready to Study!', description: 'Your textbook is complete', minProgress: 100 }
    ];
    
    // Fiction/general book steps (creative/storytelling)
    const fictionSteps = [
      { key: 'initializing', label: 'Order Received', description: 'Processing your book request', minProgress: 0 },
      { key: 'analyzing', label: 'Analyzing Concept', description: 'Understanding your vision', minProgress: 5 },
      { key: 'universe_building', label: 'World Building', description: 'Creating your story universe', minProgress: 10 },
      { key: 'story_planning', label: 'Story Planning', description: 'Developing plot structure', minProgress: 25 },
      { key: 'outline_expansion', label: 'Detailed Outline', description: 'Structuring chapters', minProgress: 40 },
      { key: 'scene_generation', label: 'Writing Scenes', description: 'Crafting your story', minProgress: 60 },
      { key: 'image_generation', label: 'Adding Illustrations', description: 'Creating visual elements', minProgress: 92 },
      { key: 'finalizing', label: 'Final Polish', description: 'Preparing your masterpiece', minProgress: 95 },
      { key: 'completed', label: 'Ready for Pickup!', description: 'Your book is complete', minProgress: 100 }
    ];
    
    // Choose appropriate steps based on book type (check multiple variations)
    const isTextbook = bookType === 'textbook' || 
                      bookType === 'Textbook' || 
                      progress?.book?.category === 'Textbook' ||
                      progress?.book?.category === 'textbook';
    
    const steps = isTextbook ? textbookSteps : fictionSteps;
    
    console.log(`📚 Using ${isTextbook ? 'TEXTBOOK' : 'FICTION'} steps for book type: ${bookType}`);
    
    return steps.map((step, index) => ({
      ...step,
      completed: progress?.progress >= step.minProgress,
      active: progress?.status === step.key,
      stepNumber: index + 1
    }));
  };

  // Estimate time based on book type and progress
  const estimateTimeRemaining = (currentProgress, bookType = 'fiction') => {
    // Base time estimates in minutes based on analysis of generator code
    const baseEstimates = {
      fiction: 4,      // Fiction: universe building, character dev, scene generation
      textbook: 15,    // Textbook: schema, plan, outline, content, expansion (3x longer)
      cookbook: 3,     // Cookbook: plan, recipes, formatting
      children_book: 3, // Children's: story, pages, illustrations
      nonfiction: 5    // Nonfiction: research, chapters, formatting
    };
    
    const baseTime = baseEstimates[bookType] || baseEstimates.fiction;
    const remaining = ((100 - currentProgress) / 100) * baseTime;
    return Math.max(0, Math.ceil(remaining));
  };

  // Format time remaining
  const formatTimeRemaining = (minutes) => {
    if (minutes <= 0) return "Almost ready!";
    if (minutes === 1) return "~1 minute";
    return `~${minutes} minutes`;
  };

  useEffect(() => {
    if (bookId) {
      console.log('BookGenerationPage mounted with bookId:', bookId);
      setStartTime(Date.now());
      
      // Add a small delay before first check to allow backend to create the book record
      const initialDelay = setTimeout(() => {
        checkProgress();
        // Start polling every 2 seconds after the initial check
        const interval = setInterval(() => {
          checkProgress();
        }, 2000);
        setPollInterval(interval);
      }, 1000); // Wait 1 second before first status check
      
      return () => {
        console.log('🧹 Cleaning up BookGenerationPage timers');
        clearTimeout(initialDelay);
        if (pollInterval) {
          console.log('🛑 Stopping polling interval');
          clearInterval(pollInterval);
        }
      };
    } else {
      console.error('BookGenerationPage: No bookId provided');
      setError('Invalid book ID. Please try again.');
      setLoading(false);
    }
  }, [bookId]);

  // Update time estimates when progress changes
  useEffect(() => {
    if (progress && progress.progress < 100) {
      const bookType = progress.book?.generator_type || 'fiction';
      const estimated = estimateTimeRemaining(progress.progress, bookType);
      setEstimatedTime(estimated);
      setTimeRemaining(formatTimeRemaining(estimated));
    } else if (progress && progress.progress >= 100) {
      setTimeRemaining("Complete!");
    }
  }, [progress]);

  // Cleanup interval when component unmounts or when completed
  useEffect(() => {
    return () => {
      if (pollInterval) {
        console.log('🛑 Component unmounting - clearing poll interval');
        clearInterval(pollInterval);
      }
    };
  }, [pollInterval]);

  const checkProgress = async () => {
    try {
      // Check for both regular user token and admin token
      const userToken = localStorage.getItem('token');
      const adminToken = localStorage.getItem('admin_token');
      const token = userToken || adminToken;
      const isAdmin = !!adminToken && !userToken;
      
      if (!token) {
        console.error('No authentication token found');
        setError('Authentication required. Please log in again.');
        setLoading(false);
        return;
      }
      
      console.log('🔍 Token detection (UPDATED VERSION):', {
        hasUserToken: !!userToken,
        hasAdminToken: !!adminToken,
        isAdmin: isAdmin,
        usingToken: token ? `${token.substring(0, 10)}...` : 'None'
      });
      
      console.log('📝 ADMIN BOOK GENERATION - Updated code is running!');
      
      if (!bookId) {
        console.error('No book ID provided');
        setError('Invalid book ID. Please try again.');
        setLoading(false);
        return;
      }
      
      console.log('Checking generation progress for book ID:', bookId);
      console.log('API endpoint:', `${API}/books/generation-status/${bookId}`);
      
      const response = await axios.get(`${API}/books/generation-status/${bookId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Generation status response:', response.data);
      setProgress(response.data);
      setLoading(false);
      
      // Set admin flag for UI display (one-time setting)
      if (isAdmin) {
        setIsAdmin(true);
      }
      
      // If completed, stop polling 
      if (response.data.status === 'completed') {
        console.log('🎉 Book generation completed! Stopping polling.');
        if (pollInterval) {
          clearInterval(pollInterval);
          setPollInterval(null);
        }
        // Let user decide when to navigate - no auto-redirect
      }
    } catch (error) {
      console.error('Error checking progress:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      // More specific error messages based on the error type
      if (error.response?.status === 401) {
        setError('Authentication expired. Please log in again.');
      } else if (error.response?.status === 404) {
        setError('Book generation not found. It may still be starting up...');
        // Only retry a few times for new generations, then stop polling
        const retryCount = (window.retryCount || 0) + 1;
        if (retryCount < 3) {
          window.retryCount = retryCount;
          setTimeout(() => {
            if (pollInterval && loading) { // Only retry if still polling and loading
              console.log(`🔄 Retrying 404... (attempt ${retryCount}/3)`);
              checkProgress();
            }
          }, 2000);
          return; // Don't set loading to false yet, will retry
        } else {
          console.log('❌ Max retries reached for 404, stopping polling');
          if (pollInterval) {
            clearInterval(pollInterval);
            setPollInterval(null);
          }
        }
      } else if (error.response?.status === 500) {
        setError('Server error. Please try refreshing the page.');
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        setError('Network connection issue. Please check your internet connection.');
      } else {
        setError(`Unable to check generation progress: ${error.response?.data?.detail || error.message}`);
      }
      
      setLoading(false);
    }
  };

  if (loading && !progress) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-purple-400 mx-auto mb-4"></div>
          <p className="text-xl">Preparing your book generation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="text-6xl mb-4">😞</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Generation Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          
          {/* Debug information */}
          <div className="bg-gray-100 rounded-lg p-4 mb-6 text-left text-sm">
            <p className="font-semibold mb-2">Debug Info:</p>
            <p>Book ID: {bookId || 'Not provided'}</p>
            <p>User Token: {localStorage.getItem('token') ? 'Present' : 'Missing'}</p>
            <p>Admin Token: {localStorage.getItem('admin_token') ? 'Present' : 'Missing'}</p>
            <p>Is Admin Mode: {isAdmin ? 'Yes' : 'No'}</p>
            <p>Backend URL: {API}</p>
          </div>
          
          <div className="space-y-3">
            <button
              onClick={() => {
                setError(null);
                setLoading(true);
                checkProgress();
              }}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium"
            >
              🔄 Try Again
            </button>
            
            <button
              onClick={() => {
                const userToken = localStorage.getItem('token');
                const adminToken = localStorage.getItem('admin_token');
                const isAdminUser = !!adminToken && !userToken;
                navigate(isAdminUser ? '/admin' : '/dashboard');
              }}
              className="w-full bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium"
            >
              📚 Check My Dashboard
            </button>
            
            <button
              onClick={() => navigate('/')}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium"
            >
              🏠 Return to Store
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
      {/* Header with Dashboard Button */}
      <header className="bg-black bg-opacity-20 backdrop-blur-sm border-b border-white border-opacity-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <h1 className="text-2xl font-bold text-white">AI Book Generation</h1>
              {isAdmin && (
                <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                  🔧 Admin Generation
                </span>
              )}
            </div>
            <div className="flex space-x-4">
              <button
                onClick={() => navigate(isAdmin ? '/admin' : '/dashboard')}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                📚 Go to {isAdmin ? 'Admin ' : ''}Dashboard
              </button>
              <button
                onClick={() => navigate('/')}
                className="text-white hover:text-purple-300 transition-colors"
              >
                ← Back to Store
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Pizza Tracker Style Progress */}
        <div className="mb-12">
          {/* Countdown Timer */}
          <div className="text-center mb-8">
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-6 inline-block">
              <div className="text-white">
                <div className="text-3xl font-bold mb-2">
                  {timeRemaining || "Calculating..."}
                </div>
                <div className="text-purple-200">
                  {progress?.status === 'completed' ? 'Your book is ready!' : 'Estimated time remaining'}
                </div>
              </div>
            </div>
          </div>

          {/* Pizza Tracker Steps */}
          <div className="relative">
            {/* Progress Line */}
            <div className="absolute top-6 left-0 right-0 h-1 bg-white bg-opacity-20 rounded-full">
              <div 
                className="h-full bg-gradient-to-r from-purple-400 to-blue-400 rounded-full transition-all duration-1000 ease-out"
                style={{ width: `${Math.max(12.5, (progress?.progress || 0) * 0.875 + 12.5)}%` }}
              ></div>
            </div>

            {/* Steps */}
            <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-2">
              {getProgressSteps().map((step, index) => (
                <div key={step.key} className="flex flex-col items-center relative">
                  <div className={`w-12 h-12 rounded-full border-3 z-10 flex items-center justify-center transition-all duration-500 ${
                    step.completed 
                      ? 'bg-green-500 border-green-500 text-white' 
                      : step.active 
                        ? 'bg-purple-500 border-purple-500 text-white animate-pulse' 
                        : 'bg-white bg-opacity-20 border-white border-opacity-40 text-white'
                  }`}>
                    {step.completed ? (
                      <span className="text-lg">✓</span>
                    ) : step.active ? (
                      <div className="w-3 h-3 bg-white rounded-full animate-ping"></div>
                    ) : (
                      <span className="text-sm font-bold">{step.stepNumber}</span>
                    )}
                  </div>
                  <div className="mt-3 text-center">
                    <div className={`text-sm font-semibold ${
                      step.completed || step.active ? 'text-white' : 'text-gray-300'
                    }`}>
                      {step.label}
                    </div>
                    <div className="text-xs text-gray-400 mt-1 max-w-20">
                      {step.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          
          {/* Book Cover & Progress Ring */}
          <div className="text-center">
            <div className="relative inline-block">
              {/* Progress Ring */}
              <div className="relative w-80 h-80 mx-auto">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                  {/* Background circle */}
                  <path
                    d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="rgba(255,255,255,0.2)"
                    strokeWidth="2"
                  />
                  {/* Progress circle */}
                  <path
                    d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="url(#gradient)"
                    strokeWidth="2"
                    strokeDasharray={`${progress?.progress || 0}, 100`}
                    className="transition-all duration-1000 ease-out"
                  />
                  <defs>
                    <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#8B5CF6" />
                      <stop offset="100%" stopColor="#06B6D4" />
                    </linearGradient>
                  </defs>
                </svg>
                
                {/* Book Cover Preview */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-48 h-64 bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg shadow-2xl flex items-center justify-center relative overflow-hidden">
                    {progress?.status === 'completed' && progress?.book?.cover_image_url ? (
                      <img
                        src={progress.book.cover_image_url?.startsWith('http') ? progress.book.cover_image_url : `${BACKEND_URL}${progress.book.cover_image_url}`}
                        alt={progress.book.title}
                        className="w-full h-full object-cover rounded-lg animate-fade-in"
                      />
                    ) : (
                      <div className="text-center text-white p-4">
                        <div className="text-4xl mb-2">
                          {progress?.status === 'title_generation' && '✍️'}
                          {progress?.status === 'author_generation' && '👤'}
                          {progress?.status === 'synopsis_generation' && '📝'}
                          {progress?.status === 'cover_generation' && '🎨'}
                          {progress?.status === 'finalizing' && '✨'}
                          {(progress?.status === 'initializing' || !progress?.status) && '📚'}
                        </div>
                        <div className="text-sm font-medium opacity-90">
                          {progress?.status === 'completed' ? 'Complete!' : 'Generating...'}
                        </div>
                      </div>
                    )}
                    
                    {/* Animated overlay for generating states */}
                    {progress?.status !== 'completed' && (
                      <div className="absolute inset-0 bg-gradient-to-br from-purple-600/30 to-blue-600/30 animate-pulse"></div>
                    )}
                  </div>
                </div>
                
                {/* Progress percentage */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-black bg-opacity-50 text-white px-4 py-2 rounded-full mt-80">
                    <span className="font-bold text-lg">{progress?.progress || 0}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Book Details & Progress Info */}
          <div className="text-white space-y-8">
            
            {/* Book Title */}
            <div>
              <h2 className="text-4xl font-bold mb-2">
                {progress?.book?.title || (
                  <span className="animate-pulse bg-white bg-opacity-20 rounded px-4 py-2 inline-block">
                    Generating title...
                  </span>
                )}
              </h2>
              <p className="text-xl text-purple-300">
                by {progress?.book?.author || (
                  <span className="animate-pulse bg-white bg-opacity-20 rounded px-2 py-1 inline-block">
                    Finding author...
                  </span>
                )}
              </p>
            </div>

            {/* Current Step */}
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-2">Current Step</h3>
              <p className="text-purple-200 mb-4">{progress?.current_step}</p>
              
              {/* Progress Bar */}
              <div className="w-full bg-white bg-opacity-20 rounded-full h-3 mb-4">
                <div 
                  className="bg-gradient-to-r from-purple-400 to-blue-400 h-3 rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${progress?.progress || 0}%` }}
                ></div>
              </div>
              
              {/* Inspiring Quote */}
              <blockquote className="italic text-purple-200 border-l-4 border-purple-400 pl-4">
                "{progress?.inspiring_quote}"
              </blockquote>
            </div>

            {/* Synopsis Preview */}
            {progress?.book?.synopsis && (
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-3">Synopsis</h3>
                <p className="text-gray-200 leading-relaxed">
                  {progress.book.synopsis}
                </p>
              </div>
            )}

            {/* Completion Actions */}
            {progress?.status === 'completed' && (
              <div className="space-y-4">
                <div className="bg-green-500 bg-opacity-20 border border-green-400 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">🎉</span>
                    <span className="text-green-300 font-semibold">Your book is ready!</span>
                  </div>
                </div>
                
                <div className="flex space-x-4">
                  <button
                    onClick={() => navigate(`/book/${progress.book.id}`)}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex-1"
                  >
                    View Book Details
                  </button>
                  <button
                    onClick={() => navigate(isAdmin ? '/admin' : '/dashboard')}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex-1"
                  >
                    Go to {isAdmin ? 'Admin ' : ''}Dashboard
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* CSS for fade-in animation */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fade-in {
          animation: fade-in 1s ease-out;
        }
      `}</style>
    </div>
  );
};
const SuccessPage = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [sessionId, setSessionId] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState('verifying'); // 'verifying', 'success', 'error', 'redirecting'
  const [purchaseDetails, setPurchaseDetails] = useState(null);

  useEffect(() => {
    // Debug: Check what user is currently authenticated
    console.log('=== SUCCESS PAGE LOADED ===');
    console.log('Current token:', localStorage.getItem('token') ? `${localStorage.getItem('token').substring(0, 10)}...` : 'None');
    console.log('Current user context:', user?.email, user?.id);
    
    // Check pre-payment stored data
    const prePaymentUser = localStorage.getItem('pre_payment_user');
    const prePaymentToken = localStorage.getItem('pre_payment_token');
    console.log('Pre-payment user data:', prePaymentUser ? JSON.parse(prePaymentUser) : 'None');
    console.log('Pre-payment token:', prePaymentToken ? `${prePaymentToken.substring(0, 10)}...` : 'None');
    
    // Check if tokens match
    const currentToken = localStorage.getItem('token');
    if (prePaymentToken && currentToken !== prePaymentToken) {
      console.error('⚠️ TOKEN MISMATCH DETECTED!');
      console.error('Pre-payment token:', prePaymentToken ? `${prePaymentToken.substring(0, 10)}...` : 'None');
      console.error('Current token:', currentToken ? `${currentToken.substring(0, 10)}...` : 'None');
      
      // CRITICAL: Reject mismatched tokens and redirect to login
      console.error('❌ SECURITY VIOLATION: Token mismatch detected - clearing all data and redirecting to login');
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/?error=session_expired';
      return;
    } else {
      console.log('✅ Token consistency check passed');
    }
    
    // Get session_id from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const session_id = urlParams.get('session_id');
    
    if (session_id) {
      setSessionId(session_id);
      verifyPurchase(session_id);
    } else {
      setVerificationStatus('error');
    }
  }, []);

  const verifyPurchase = async (session_id) => {
    console.log('=== Starting Payment Verification ===');
    console.log('Session ID:', session_id);
    const currentToken = localStorage.getItem('token');
    console.log('Current token:', currentToken ? `${currentToken.substring(0, 10)}...` : 'None');
    
    try {
      // First try to verify as a book purchase
      let response;
      try {
        const token = localStorage.getItem('token');
        response = await axios.post(`${API}/purchase/verify`, null, {
          params: { session_id },
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        
        if (response.data.success) {
          setVerificationStatus('success');
          setPurchaseDetails({
            ...response.data,
            type: response.data.generation_url ? 'generated_book_purchase' : 'book_purchase'
          });

          // Clear stored pre-payment data now that purchase succeeded
          localStorage.removeItem('pre_payment_user');
          localStorage.removeItem('pre_payment_token');
          
          // Refresh user data to update credits/subscription status
          console.log('=== Before refreshUser call ===');
          console.log('Current user context:', user?.email, user?.id);
          console.log('About to refresh user data - ensuring token integrity');
          
          // CRITICAL SECURITY: Store current user ID before refresh
          const userIdBeforeRefresh = user?.id;
          
          await refreshUser();
          
          console.log('=== After refreshUser call ===');
          console.log('Updated user context:', user?.email, user?.id);
          
          // CRITICAL SECURITY CHECK: Verify user identity hasn't changed after refresh
          if (userIdBeforeRefresh && user?.id && user.id !== userIdBeforeRefresh) {
            console.error('🚨 CRITICAL SECURITY BREACH: User ID changed during subscription upgrade!');
            console.error('User ID before refresh:', userIdBeforeRefresh);
            console.error('User ID after refresh:', user.id);
            console.error('Forcing logout and redirect to prevent account confusion');
            
            localStorage.clear();
            sessionStorage.clear();
            window.location.href = '/?error=identity_mismatch';
            return;
          }
          
          console.log('✅ User identity verification passed during subscription upgrade');
          
          // If this is a generated book purchase, redirect to generation page immediately
          if (response.data.generation_url) {
            console.log('Book generation purchase verified. Book ID:', response.data.book_id);
            console.log('Redirecting to generation page:', response.data.generation_url);
            setVerificationStatus('redirecting');
            
            // Show redirecting message briefly then redirect
            setTimeout(() => {
              try {
                navigate(response.data.generation_url);
              } catch (error) {
                console.error('Navigation error:', error);
                // Fallback redirect using book_id
                if (response.data.book_id) {
                  navigate(`/generating/${response.data.book_id}`);
                } else {
                  navigate('/dashboard');
                }
              }
            }, 1500); // Reduced to 1.5 seconds for faster redirect
            return;
          }
          
          return;
        }
      } catch (error) {
        // If book purchase verification fails, try subscription verification
        console.log('Not a book purchase, trying subscription verification...');
      }
      
      // Try to verify as a subscription
      try {
        const token = localStorage.getItem('token');
        response = await axios.post(`${API}/payments/verify-subscription`, null, {
          params: { session_id },
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        
        if (response.data.success) {
          setVerificationStatus('success');
          setPurchaseDetails({
            ...response.data,
            type: 'subscription'
          });

          // Clear stored pre-payment data now that purchase succeeded
          localStorage.removeItem('pre_payment_user');
          localStorage.removeItem('pre_payment_token');
          
          // Refresh user data to update credits/subscription status
          console.log('=== Before refreshUser call ===');
          console.log('Current user context:', user?.email, user?.id);
          console.log('About to refresh user data - ensuring token integrity');
          
          // CRITICAL SECURITY: Store current user ID before refresh
          const userIdBeforeRefresh = user?.id;
          
          await refreshUser();
          
          console.log('=== After refreshUser call ===');
          console.log('Updated user context:', user?.email, user?.id);
          
          // CRITICAL SECURITY CHECK: Verify user identity hasn't changed after refresh
          if (userIdBeforeRefresh && user?.id && user.id !== userIdBeforeRefresh) {
            console.error('🚨 CRITICAL SECURITY BREACH: User ID changed during subscription upgrade!');
            console.error('User ID before refresh:', userIdBeforeRefresh);
            console.error('User ID after refresh:', user.id);
            console.error('Forcing logout and redirect to prevent account confusion');
            
            localStorage.clear();
            sessionStorage.clear();
            window.location.href = '/?error=identity_mismatch';
            return;
          }
          
          console.log('✅ User identity verification passed during subscription upgrade');
          
          return;
        }
      } catch (error) {
        console.log('Not a subscription either...');
      }
      
      // If both failed, set error
      setVerificationStatus('error');
      
    } catch (error) {
      console.error('Error verifying purchase:', error);
      setVerificationStatus('error');
    }
  };

  if (verificationStatus === 'verifying') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-lg text-gray-600">Verifying your purchase...</p>
        </div>
      </div>
    );
  }

  if (verificationStatus === 'redirecting') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="text-6xl mb-6">🎨</div>
          <h1 className="text-3xl font-bold mb-4">Your Book is Being Generated!</h1>
          <p className="text-xl mb-6">Redirecting you to watch the magical creation process...</p>
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-400 mx-auto mb-8"></div>
          
          {/* Manual redirect button as backup */}
          <button
            onClick={() => {
              if (purchaseDetails?.generation_url) {
                navigate(purchaseDetails.generation_url);
              } else if (purchaseDetails?.book_id) {
                navigate(`/generating/${purchaseDetails.book_id}`);
              } else {
                navigate('/dashboard');
              }
            }}
            className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            Click here if not redirected automatically
          </button>
        </div>
      </div>
    );
  }

  if (verificationStatus === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Payment Verification Failed</h1>
          <p className="text-gray-600 mb-6">
            We couldn't verify your payment. Please contact support if you were charged.
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium"
          >
            Return to Store
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/')}
            className="text-purple-600 hover:text-purple-800 font-medium"
          >
            ← Back to Store
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          {/* Success Animation */}
          <div className="text-6xl mb-6">🎉</div>
          
          <h1 className="text-3xl font-bold text-green-600 mb-4">
            {purchaseDetails?.type === 'subscription' ? 'Subscription Activated!' : 
             purchaseDetails?.type === 'generated_book_purchase' ? 'Generation Started!' : 'Payment Successful!'}
          </h1>
          
          <p className="text-lg text-gray-700 mb-8">
            {purchaseDetails?.type === 'subscription' 
              ? 'Welcome to the Storage Plan! You can now save and manage your AI-generated books.'
              : purchaseDetails?.type === 'generated_book_purchase'
              ? 'Your custom AI book is being generated! You\'ll be redirected to watch the progress shortly.'
              : 'Thank you for your purchase! Your payment has been processed successfully.'
            }
          </p>

          {/* Purchase Details */}
          {purchaseDetails && (
            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {purchaseDetails.type === 'subscription' ? 'Subscription Details' : 'Purchase Details'}
              </h3>
              <div className="space-y-2 text-sm">
                {purchaseDetails.type === 'subscription' ? (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Subscription ID:</span>
                      <span className="text-gray-900 font-mono">{purchaseDetails.subscription_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Plan:</span>
                      <span className="text-gray-900 capitalize">{purchaseDetails.plan}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <span className="text-gray-900 capitalize">{purchaseDetails.status}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Amount:</span>
                      <span className="text-gray-900">$15.00/month</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Order ID:</span>
                      <span className="text-gray-900 font-mono">{purchaseDetails.order_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Book ID:</span>
                      <span className="text-gray-900 font-mono">{purchaseDetails.book_id}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Session ID:</span>
                  <span className="text-gray-900 font-mono text-xs">{sessionId}</span>
                </div>
              </div>
            </div>
          )}

          {/* Next Steps */}
          <div className="bg-blue-50 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-blue-900 mb-3">What's Next?</h3>
            <p className="text-blue-800 mb-4">
              {purchaseDetails?.message || 
               (purchaseDetails?.type === 'subscription' 
                 ? 'Your storage plan is now active! You can generate and save unlimited books.'
                 : 'Your book is being generated and will be available for download shortly.'
               )}
            </p>
            <div className="space-y-2 text-sm text-blue-700">
              {purchaseDetails?.type === 'subscription' ? (
                <>
                  <p>• Save unlimited AI-generated books</p>
                  <p>• Access your collection anytime</p>
                  <p>• Manage your subscription in the dashboard</p>
                  <p>• Priority customer support</p>
                </>
              ) : (
                <>
                  <p>• You'll receive an email confirmation</p>
                  <p>• Your book will be available in your dashboard</p>
                  <p>• Generation typically takes 2-5 minutes</p>
                </>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-4 sm:space-y-0 sm:space-x-4 sm:flex sm:justify-center">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
            >
              View My Dashboard
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full sm:w-auto bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Credits Purchase Modal Component
const CreditsPurchaseModal = ({ isOpen, onClose, bookData, onPurchase }) => {
  const [purchasing, setPurchasing] = useState(false);

  if (!isOpen || !bookData) return null;

  const handlePurchase = async () => {
    setPurchasing(true);
    try {
      await onPurchase(bookData);
    } catch (error) {
      console.error('Purchase failed:', error);
    } finally {
      setPurchasing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">No Credits Available</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
          
          <div className="mb-6">
            <div className="text-center mb-4">
              <div className="text-4xl mb-2">🎯</div>
              <p className="text-gray-700">
                You don't have enough credits to generate a book.
              </p>
            </div>
            
            <div className="bg-purple-50 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-purple-900 mb-2">Your Book Details</h4>
              <p className="text-sm text-purple-800">
                <strong>Category:</strong> {bookData.category}
              </p>
              <p className="text-sm text-purple-800">
                <strong>Prompt:</strong> "{bookData.prompt}"
              </p>
            </div>
            
            <div className="bg-green-50 rounded-lg p-4">
              <h4 className="font-semibold text-green-900 mb-2">💡 Solution</h4>
              <p className="text-sm text-green-800 mb-3">
                Purchase this custom AI-generated book for <strong>$9.99</strong>
              </p>
              <ul className="text-xs text-green-700 space-y-1">
                <li>✅ Same book based on your prompt</li>
                <li>✅ Professional AI-generated cover</li>
                <li>✅ Compelling synopsis</li>
                <li>✅ Instant download</li>
              </ul>
            </div>
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handlePurchase}
              disabled={purchasing}
              className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {purchasing ? 'Setting up...' : 'Buy for $9.99'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function BookStore() {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, refreshUser } = useAuth();
  const { addNotification } = useNotifications();
  const [books, setBooks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [skip, setSkip] = useState(0);
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showCreditsPurchase, setShowCreditsPurchase] = useState(null);

  // Load initial data
  useEffect(() => {
    loadCategories();
    // Only load books once on initial mount
    if (books.length === 0) {
      loadBooks();
      seedBooksIfEmpty();
    }
  }, []);

  // Load books when category changes (but not on initial mount)
  useEffect(() => {
    // Skip if this is the initial mount (books array is empty)
    if (books.length > 0 || selectedCategory !== 'All') {
      setSkip(0);
      loadBooks(true);
    }
  }, [selectedCategory]);

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadBooks = async (reset = false) => {
    try {
      setLoading(true);
      
      console.log(`Loading books: reset=${reset}, currentBooks=${books.length}, category=${selectedCategory}`);
      
      const currentSkip = reset ? 0 : skip;
      const category = selectedCategory === 'All' ? '' : selectedCategory;
      
      const response = await axios.get(`${API}/books`, {
        params: { category, limit: 12, skip: currentSkip }
      });

      console.log(`API returned ${response.data.books.length} books`);

      if (reset) {
        setBooks(response.data.books);
        console.log(`Reset books to ${response.data.books.length} items`);
      } else {
        // Deduplicate books before adding them
        setBooks(prev => {
          const existingIds = new Set(prev.map(book => book.id));
          const newBooks = response.data.books.filter(book => !existingIds.has(book.id));
          console.log(`Adding ${newBooks.length} new books (${response.data.books.length - newBooks.length} duplicates filtered)`);
          return [...prev, ...newBooks];
        });
      }
      
      setHasMore(response.data.has_more);
      setSkip(currentSkip + 12);
    } catch (error) {
      console.error('Error loading books:', error);
    } finally {
      setLoading(false);
    }
  };

  const seedBooksIfEmpty = async () => {
    try {
      await axios.post(`${API}/books/seed`);
    } catch (error) {
      console.error('Error seeding books:', error);
    }
  };

  const handleGenerateBook = async (formData) => {
    if (!isAuthenticated) {
      setShowLogin(true);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API}/books/generate`, {
        prompt: formData.prompt,
        category: formData.category
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Refresh user's credit information after successful generation
      await refreshUser();
      
      // Redirect to the beautiful generation page
      navigate(`/generating/${response.data.book_id}`);
      
    } catch (error) {
      console.error('Error generating book:', error);
      const errorMsg = error.response?.data?.detail || 'Error generating book';
      
      if (errorMsg.includes('Insufficient credits')) {
        // Set state to show purchase option in UI instead of popup
        setShowCreditsPurchase({
          prompt: formData.prompt,
          category: formData.category
        });
      } else if (errorMsg.includes('Authentication required')) {
        setShowLogin(true);
      } else {
        addNotification(`Generation failed: ${errorMsg}`, 'error');
      }
    }
  };

  const handleCreditsPurchase = async (bookData) => {
    try {
      const token = localStorage.getItem('token');
      
      const checkoutResponse = await axios.post(`${API}/purchase/create-generated-book-checkout`, {
        prompt: bookData.prompt,
        category: bookData.category
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to Stripe checkout
      window.location.href = checkoutResponse.data.checkout_url;
      
    } catch (error) {
      console.error('Error creating checkout:', error);
      addNotification('Error setting up payment. Please try again.', 'error');
    }
  };

  const handlePurchase = async (book) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/purchase/create-checkout`, {
        book_id: book.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
      
    } catch (error) {
      console.error('Error creating checkout:', error);
      const errorMsg = error.response?.data?.detail || 'Error processing purchase';
      
      if (errorMsg.includes('already own this book')) {
        addNotification('You already own this book! Check your dashboard to access it.', 'warning');
      } else if (error.response?.status === 500 && errorMsg.includes('temporarily unavailable')) {
        addNotification('Payment system is in test mode. Please try again.', 'warning');
      } else {
        addNotification(`Purchase failed: ${errorMsg}`, 'error');
      }
    }
  };

  const handleGenerateFromBook = async (book) => {
    alert(`This will trigger your book generator with:\n\nTitle: ${book.title}\nCategory: ${book.category}\nPrompt: ${book.prompt}\n\nYour existing book generator will create a full PDF book!`);
  };

  const loadMoreBooks = () => {
    if (!loading && hasMore) {
      loadBooks();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
        {/* Enhanced Header with BookPress.ai Branding */}
        <header className="bg-white shadow-sm border-b sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              
              {/* BookPress.ai Logo & Brand */}
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  {/* Creative Logo Symbol */}
                  <div className="relative">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg flex items-center justify-center shadow-md">
                      <div className="text-white font-bold text-lg leading-none">
                        <span className="block transform -rotate-12">📚</span>
                      </div>
                    </div>
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-orange-400 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-bold">AI</span>
                    </div>
                  </div>
                  
                  {/* Brand Name */}
                  <div className="hidden sm:block">
                    <h1 className="text-xl font-bold">
                      <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                        BookPress
                      </span>
                      <span className="text-orange-500 font-mono">.ai</span>
                    </h1>
                    <p className="text-xs text-gray-500 -mt-1">AI-Powered Publishing</p>
                  </div>
                  
                  {/* Mobile Brand */}
                  <div className="sm:hidden">
                    <h1 className="text-lg font-bold">
                      <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                        BookPress
                      </span>
                      <span className="text-orange-500 font-mono">.ai</span>
                    </h1>
                  </div>
                </div>
              </div>

              {/* Navigation & User Menu */}
              <div className="flex items-center space-x-4">
                
                {isAuthenticated ? (
                  <>
                    {/* User Info - Clean & Compact */}
                    <div className="hidden md:flex items-center space-x-3">
                      <div className="text-right">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-900">{user?.email}</span>
                          <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                            {user?.credits || 0} credits
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {user?.plan === 'storage' ? '✨ Storage Plan' : 'Free Plan'}
                        </div>
                      </div>
                    </div>
                    
                    {/* Mobile User Menu */}
                    <div className="md:hidden">
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                        {user?.credits || 0}
                      </span>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => navigate('/dashboard')}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                      >
                        <span className="hidden sm:inline">Dashboard</span>
                        <span className="sm:hidden">📊</span>
                      </button>
                      <button
                        onClick={logout}
                        className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                        title="Logout"
                      >
                        <span className="hidden sm:inline">Logout</span>
                        <span className="sm:hidden">🚪</span>
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => setShowLogin(true)}
                      className="text-gray-700 hover:text-gray-900 px-3 py-2 text-sm font-medium transition-colors duration-200"
                    >
                      Login
                    </button>
                    <button
                      onClick={() => setShowRegister(true)}
                      className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                    >
                      Get Started
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Book Generator */}
        <BookGenerator
          categories={categories}
          onGenerate={handleGenerateBook}
          isGenerating={generating}
        />

        {/* Category Filter */}
        <CategoryFilter
          categories={categories}
          selectedCategory={selectedCategory}
          onSelectCategory={setSelectedCategory}
        />

        {/* Books Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mb-12">
          {books.map((book) => (
            <BookCard
              key={`store-book-${book.id}`}
              book={book}
              onPurchase={handlePurchase}
              onGenerate={handleGenerateFromBook}
            />
          ))}
        </div>

        {/* Loading */}
        {loading && <LoadingSpinner />}

        {/* Load More Button */}
        {!loading && hasMore && (
          <div className="text-center">
            <button
              onClick={loadMoreBooks}
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-8 rounded-lg transition-colors duration-200"
            >
              Show Me More Books
            </button>
          </div>
        )}

        {/* No More Books */}
        {!loading && !hasMore && books.length > 0 && (
          <div className="text-center py-8">
            <p className="text-gray-600 text-lg">You've seen all the books in this category!</p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-2xl font-bold mb-4">AI Book Store</h3>
          <p className="text-gray-400">
            Experience the future of reading with AI-generated books
          </p>
        </div>
      </footer>
      
      {/* Auth Modals */}
      {showLogin && <LoginForm onClose={() => setShowLogin(false)} />}
      {showRegister && <RegisterForm onClose={() => setShowRegister(false)} />}
      
      {/* Credits Purchase Modal */}
      <CreditsPurchaseModal
        isOpen={!!showCreditsPurchase}
        onClose={() => setShowCreditsPurchase(null)}
        bookData={showCreditsPurchase}
        onPurchase={handleCreditsPurchase}
      />
    </div>
  );
}

// Global debugging function for console access
window.clearAllAuthData = () => {
  console.log('=== MANUAL CLEANUP OF ALL AUTH DATA ===');
  
  // Clear localStorage
  console.log('Clearing localStorage...');
  localStorage.clear(); // Use clear() instead of individual removes
  
  // Clear sessionStorage  
  console.log('Clearing sessionStorage...');
  sessionStorage.clear();
  
  // Clear all cookies
  console.log('Clearing cookies...');
  document.cookie.split(";").forEach(function(c) { 
    document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
  });
  
  console.log('✅ All auth data cleared. Please refresh the page and log in again.');
  alert('All cached data cleared. Page will refresh now.');
  window.location.href = '/';
};

// Global function to check auth state
window.checkAuthState = () => {
  console.log('=== CURRENT AUTH STATE ===');
  console.log('localStorage token:', localStorage.getItem('token') ? `${localStorage.getItem('token').substring(0, 10)}...` : 'None');
  console.log('sessionStorage token:', sessionStorage.getItem('token') ? `${sessionStorage.getItem('token').substring(0, 10)}...` : 'None');
  console.log('Cookies:', document.cookie);
  console.log('All localStorage keys:', Object.keys(localStorage));
  console.log('All sessionStorage keys:', Object.keys(sessionStorage));
};

function App() {
  return (
    <NotificationProvider>
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/" element={<BookStore />} />
            <Route path="/book/:bookId" element={<BookDetailPage />} />
            <Route path="/dashboard" element={<UserDashboard />} />
            <Route path="/success" element={<SuccessPage />} />
            <Route path="/generating/:bookId" element={<BookGenerationPage />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />
          </Routes>
        </Router>
      </AuthProvider>
    </NotificationProvider>
  );
}

export default App;